import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Layout from '../../wrappers/layout';

import IndicatorCard from '../dashboard/components/indicator-card';
import BuySellMetals from '../dashboard/components/buy-sell-metals';
import PageHeader from '../../shared/page-header';
import MetalTransactions from '../passbook/components/metal-transactions';
import { passbookStore } from '../../store/PassbookStore';
import PassbookService from '../../services/passbook.service';
import { Pagination } from '../../shared/common/pagination';
import Loader from '../../shared/common/loader';
import DashboardService from '../../services/dashboard.service';
import { dashboardStore } from '../../store/DashboardStore';
import { userStore } from '../../store/UserStore';
import { formatAmount } from '../../utils';
import NomineeModal from 'pages/profile/components/NomineeModal';
import SupportButton from 'shared/common/supportButton/SupportButton';
import { toast } from 'react-toastify';

interface PropsType { }

const metalsWithCode = [
  { code: 'Au', name: 'Gold' },
  { code: 'Ag', name: 'Silver' },
  { code: 'Pt', name: 'Platinum' },
  { code: 'Pd', name: 'Palladium' },
];

const SkeletonPulse = ({ style = {} }: { style?: React.CSSProperties }) => (
  <div
    style={{
      backgroundColor: '#e0e0e0',
      borderRadius: '6px',
      animation: 'skeleton-pulse 1.4s ease-in-out infinite',
      ...style,
    }}
  />
);

const IndicatorCardSkeleton = ({ dark = false }: { dark?: boolean }) => (
  <div
    style={{
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: dark ? '#301250' : '#f5f5f5',
      minHeight: '110px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}
  >
    <SkeletonPulse
      style={{
        height: '14px',
        width: '60%',
        backgroundColor: dark ? 'rgba(255,255,255,0.15)' : '#e0e0e0',
      }}
    />
    <SkeletonPulse
      style={{
        height: '28px',
        width: '75%',
        backgroundColor: dark ? 'rgba(255,255,255,0.2)' : '#d4d4d4',
      }}
    />
    <SkeletonPulse
      style={{
        height: '12px',
        width: '40%',
        backgroundColor: dark ? 'rgba(255,255,255,0.1)' : '#e8e8e8',
      }}
    />
  </div>
);

const BuySellSkeleton = () => (
  <div
    style={{
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: '#f9f9f9',
      border: '1px solid #ececec',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      minHeight: '340px',
    }}
  >
    {/* Metal selector tabs */}
    <div style={{ display: 'flex', gap: '8px' }}>
      {[1, 2, 3].map((i) => (
        <SkeletonPulse key={i} style={{ height: '32px', flex: 1, borderRadius: '8px' }} />
      ))}
    </div>
    {/* Buy/Sell toggle */}
    <div style={{ display: 'flex', gap: '8px' }}>
      <SkeletonPulse style={{ height: '36px', flex: 1, borderRadius: '8px' }} />
      <SkeletonPulse style={{ height: '36px', flex: 1, borderRadius: '8px' }} />
    </div>
    {/* Amount input */}
    <SkeletonPulse style={{ height: '44px', width: '100%', borderRadius: '8px' }} />
    {/* Rate info */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <SkeletonPulse style={{ height: '12px', width: '80%' }} />
      <SkeletonPulse style={{ height: '12px', width: '60%' }} />
    </div>
    {/* Submit button */}
    <SkeletonPulse style={{ height: '44px', width: '100%', borderRadius: '8px', marginTop: 'auto' }} />
  </div>
);


const BuyAndSell: React.FC<PropsType> = () => {
  const [metaData, setMetaData] = useState<any>({});
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [loading, setLoading] = useState<any>(false);
  const [cardsLoading, setCardsLoading] = useState<boolean>(true);
  const [selectedMetalData, setSelectedMetalData] = useState<any>(null);
  const [accountHolding, setAccountHolding] = useState<any>([]);

  const [metalTransactions, setMetalTransactions] = passbookStore(
    (state: any) => [state.metalTransactions, state.setMetalTransactions]
  );
  const [metals, summary, customerHolding, setMetals, setSummary, setCustomerHolding] =
    dashboardStore((state: any) => [
      state.metals,
      state.summary,
      state.customerHolding,
      state.setMetals,
      state.setSummary,
      state.setCustomerHolding,
    ]);

  const [customerId, user, selectedAccount, customer] = userStore(
    (state: any) => [
      state.customerId,
      state.user,
      state.selectedAccount,
      state.customer,
    ]
  );

  const [customerInvestedValue, setCustomerInvestedValue] = useState(0);
  const [metalLoading, setMetalLoading] = useState<boolean>(true);

  useEffect((): any => {
    document.title = 'Purchase Metals - eBullion';
    setLoading(true);
    setMetalLoading(true);

    // Fast ones — invested value + transactions
    Promise.all([
      getCustomerHoldings(),
      getMetalTransactions(currentPage),
    ]).finally(() => setLoading(false));

    // Slow ones — metal card + buy/sell panel
    Promise.all([
      getPassbookSummary(),
      getAllMetals(),
    ]).finally(() => setMetalLoading(false));

  }, []);

  useEffect((): any => {
    setLoading(true);
    Promise.all([getMetalTransactions(currentPage)])
      .then(() => {
        getAccountRelatedMetalValues();
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [selectedAccount]);

  const getMetalTransactions = async (page: number) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getMetalTransactions(
        customerId,
        page,
        selectedAccount.accountId,
        user?.userId
      );
      if (res.status === 200 && res.data.data) {
        setCurrentPage(page);
        setMetaData({
          current: res.data.meta.page,
          pages: res.data.meta.totalPages + 1,
        });
        const rawResult: any[] = res?.data?.data?.result ?? [];
        const normalized = rawResult.flatMap((entry: any) => {
          if (entry.orders && Array.isArray(entry.orders)) {
            return entry.orders.map((subOrder: any) => ({
              ...subOrder,
              mmParentOrderRef: entry.ebOrderRef,
              isGrouped: true,
            }));
          }
          return [{ ...entry, isGrouped: false }];
        });
        setMetalTransactions(normalized);
        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerHoldings = async () => {
    try {
      const payload = { customerId, partnerId: customer?.partner?.id };
      const res = await DashboardService.getInvestmentValue(payload);
      setCustomerInvestedValue(res?.data?.data?.totalInvestmentAmount);
    } catch (error) {
      toast.error('Something went wrong!');
    }
  };

  const getAllMetals = async () => {
    try {
      const res: any = await DashboardService.getAllMetals();
      if (res.status === 200 && res.data.data.result.length > 0) {
        setMetals(res.data.data.result);
        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    }
  };

  /**
   * Get passbook summary details
   */
  const getPassbookSummary = async () => {
    try {
      const res: any = await DashboardService.getPassbookSummary(customerId, user?.userId);
      if (res.status === 200 && res.data.data) {
        setSummary(res.data.data.summary);
        setAccountHolding(res.data.data.metal.accountsHolding);
        if (res.data.data.metal.customerHolding.length > 0) {
          setSelectedMetalData(
            res.data.data.metal.customerHolding.filter((x: any) => x.metal === 'Ag').length > 0
              ? res.data.data.metal.customerHolding.filter((x: any) => x.metal === 'Ag')[0]
              : null
          );
        }
        setCustomerHolding(res.data.data.metal.customerHolding);
        return Promise.resolve(true);
      } else {
        return Promise.reject(false);
      }
    } catch (e: any) {
      console.log(e.message);
      return Promise.reject(false);
    }
  };

  const getAccountRelatedMetalValues = () => {
    let accountHoldingData = accountHolding.filter(
      (item: any) => item.accountId === selectedAccount.accountId
    );
    if (accountHoldingData.length > 0) {
      setSelectedMetalData(
        accountHoldingData[0].accountHolding.filter(
          (x: any) => x.metal === selectedMetalData.metal
        ).length > 0
          ? accountHoldingData[0].accountHolding.filter(
            (x: any) => x.metal === selectedMetalData.metal
          )[0]
          : null
      );
      setCustomerHolding(accountHoldingData[0].accountHolding);
    }
  };

  const setSelectedMetalValues = (value) => {
    setSelectedMetalData(
      customerHolding.filter((x: any) => x.metal === value).length > 0
        ? customerHolding.filter((x: any) => x.metal === value)[0]
        : null
    );
  };

  return (
    <Layout>
      {/* Skeleton pulse keyframe */}
      <style>{`
        @keyframes skeleton-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <PageHeader
        mainTitle='Buy and Sell'
        subTitle='Below are your transaction details'
      />

      <div>
        <Row>
          <Col md={8}>
            <Row>
              <Col md={6}>
                <div className='my-1 col col-sm-12'>
                 
                    <IndicatorCard
                      isMain
                      heading={
                        <span className='metal-heading'>
                          Your Invested Value
                          <span className='metal-note'>*(Exclusive of GST)</span>
                        </span>
                      }
                      amount={`₹ ${formatAmount(customerInvestedValue) || '0.00'}`}
                      backgroundColor='#301250'
                      addClassName='tot-card'
                      styles=''
                    />
                </div>
              </Col>
              <Col md={6}>
                <div className='my-1 col col-sm-12'>
                  {metalLoading ? (
                    <IndicatorCardSkeleton />
                  ) : (
                    selectedMetalData && (
                      <IndicatorCard
                        isMain={false}
                        heading={
                          metalsWithCode.filter((x: any) => x.code === selectedMetalData.metal).length > 0
                            ? metalsWithCode.filter((x: any) => x.code === selectedMetalData.metal)[0].name
                            : ''
                        }
                        amount={`₹ ${formatAmount(selectedMetalData.value) || ' 0.00'}`}
                        quantity={`${selectedMetalData.quantity.toFixed(4)}`}
                        addClassName={`${selectedMetalData.metal} metal`}
                        earmarkedBalance={selectedMetalData.lockQuantity || ''}
                      />
                    )
                  )}
                </div>
              </Col>
            </Row>

            <Row className='mt-3'>
              <Col md={12}>
                <h5>Past Buy & Sell Transactions</h5>
                <div className='t-wrap overflow-auto'>
                    <MetalTransactions
                      metalData={metalTransactions}
                      type='Past Buy & Sell Transactions'
                    />
                </div>
                <Row>
                  <Col md={12}>
                    {!loading && metaData.pages > 1 && (
                      <Pagination
                        meta={metaData}
                        handlePageChange={(number: number) => {
                          setLoading(true);
                          getMetalTransactions(number).finally(() => setLoading(false));
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col md={4} className='py-2'>
            {metalLoading ? (
              <BuySellSkeleton />
            ) : (
              selectedMetalData && (
                <BuySellMetals
                  metals={metals}
                  walletBalance={summary.walletBalance}
                  isDashboard={false}
                  setSelectedMetalData={setSelectedMetalValues}
                  getPassbookSummary={getPassbookSummary}
                  selectedMetalData={selectedMetalData}
                  getMetalTransactions={getMetalTransactions}
                />
              )
            )}
          </Col>
        </Row>

        <SupportButton />
      </div>
    </Layout>
  );
};

export default BuyAndSell;